import { Link } from 'react-router-dom'
import { ArrowLeft, Image } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { TextToImageGallery } from '@/components/gallery/TextToImageGallery'
import { ImageGalleryItem } from '@/models/gallery'
import { TextImageToImageGallery } from '@/components/gallery/TextImageToImageGallery'
import { styles } from '@/styles/ImagePage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Qwen Image',
    shortName: 'Qwen Image',
    modelKey: 'qwen-image',
    creditsPerImage: 2,
    heroBgImage: 'https://cdn.aibrush.co/docs/images/models/qwen-image/image-hero.jpg',
    providerUrl: 'https://huggingface.co/Qwen/Qwen-Image/',
    providerName: 'Alibaba Cloud Qwen',
    providerEditUrl: 'https://huggingface.co/Qwen/Qwen-Image-Edit/',
    pageUrl: 'https://docs.aibrush.co/images-models/qwen-image',
} as const;

const textToImageGalleryItems: ImageGalleryItem[] = [
    // {
    //     id: "qwen-image-1",
    //     width: 1536,
    //     height: 864,
    //     src: "/images/models/qwen-image/gallery-1.jpg",
    //     className: "gallery-item featured",
    //     prompt: "A woman in profile standing gracefully with eyes closed, wearing a flowing artistic textured dress made of layered organic fabric, ultra- detailed folds, cinematic lighting.Behind her, a herd of black horses galloping in motion blur, dramatic energy, desert salt flat background, golden hour sunlight, hyperrealistic, ultra - realistic photography, cinematic depth of field, masterpiece"
    // },
    {
        id: "qwen-image-2",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-2.webp",

        prompt: "A mesmerizing hyper-realistic digital art by artist PJ, featuring an egg cracked open to reveal the miniature, intricate city of Rorschach in Switzerland within.The eggshell fragments, both upright and flat, create a chaotic yet harmonious composition against the smooth, white background. The vibrant cityscape, with the Bodensee, Swiss mountains, Rorschach Harbor,and the most charming Rorschach landmarks, contrasts beautifully with the muted tones of the kitchen counter in the background. The artist's use of textures and shadows adds depth, while the vibrant colors and gritty style make this scene truly captivating, kitchen background"
    },
    {
        id: "qwen-image-3",
        width: 864,
        height: 1536,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-3.jpg",
        variant: 'tall',
        prompt: `An Anime-style cinematic scene of a young boy wearing a bright blue shirt, leaning out of a vintage train window, smiling with joy, with wind blowing through his hair.
The train rides along a mountain railway bridge with green valleys, scattered houses, and snow-capped peaks in the distance.
Bright blue sky with fluffy white clouds, vibrant flowers along the hillside.
Makoto Shinkai-inspired, Ghibli-style art, highly detailed, dreamy atmosphere, vibrant colors, soft sunlight, ultra-realistic anime illustration.`
    },
    {
        id: "qwen-image-4",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-4.jpg",

        prompt: "A heartwarming scene of a baby kitten gently touching its mother's face with a small paw, while the mother cat lies peacefully with her eyes half-closed. The lighting is soft and golden, creating a warm, emotional atmosphere. The background is blurred, emphasizing the bond between the two cats. The mood should convey tenderness, love, and the idea of returning the love once received. Capture the moment in cinematic, high-resolution detail."
    },
    {
        id: "qwen-image-5",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-5.jpg",

        prompt: "A woman made entirely of glass that is beginning to crack"
    },
    {
        id: "qwen-image-6",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-6.jpg",

        prompt: "in the style of gabriel isak imagine surreal painting woman with beautiful eyes and face and a cronw of flowers and animals on her head, the Excellence never comes from doing more things, but from doing fewer things with greater depth and intensity."
    },
    {
        id: "qwen-image-7",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-7.jpg",
        variant: 'featured',
        prompt: "A women's Rolex watch with a pink strap, a sleek gold case, and a single side gemstone floating in a pink cloud. Soma."
    },
    {
        id: "qwen-image-8",
        width: 864,
        height: 1536,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-8.jpg",
        variant: 'tall',
        prompt: "A full-body portrait of a stunning and sophisticated 50-year-old American woman, styled with an elegant gray updo hairstyle. She is walking confidently through a quiet New York City street, wearing a chic tweed jacket over a white top, paired with a flowing layered blue tulle skirt and stylish white heels. She carries a small handbag and wears fashionable sunglasses. The atmosphere is refined, modern, and chic, exuding timeless elegance and confidence. The image is ultra-realistic, cinematic street fashion photography, captured in natural daylight with 8K detail."
    },
    {
        id: "qwen-image-9",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-9.jpg",

        prompt: `A majestic tiger strides through tall grass at dawn, illuminated by warm golden light.
- **Fur texture:** *Realistic* and detailed, showcasing the tiger's natural sheen.
- **Lighting:** Warm golden light creates a serene, early‑morning atmosphere.
- **Framing:** Cinematic vertical framing emphasizes the tiger's stature and the surrounding grass.`
    },
    {
        id: "qwen-image-10",
        width: 864,
        height: 1536,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-10.jpg",
        variant: 'tall',
        prompt: "A vertical promotional poster for a vape product named 'Bar Fuel', featuring a 10ml vape bottle mid‑air with dynamic perspective. Surrounding it are vivid explosions of three distinct fruits: sliced lemons with juicy pulp and zest, golden yuzu halves with textured rinds, and ripe strawberries with seeds and juice droplets. All fruits burst mid‑air in detailed splashes of yellow, gold, and red juice around the bottle. The background features swirling smoke in citrus yellow and red, plus glitch effects, neon light rings, and digital gaming HUD elements. Created using 3D product rendering, high‑speed fruit‑splash photography, citrus texture overlays, neon vaporwave lighting, glitch UI elements, and poster layout design."
    },
    {
        id: "qwen-image-11",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-11.jpg",

        prompt: "full body stop motion female character made of clay hold a giant book, some clay on the ground"
    },
    {
        id: "qwen-image-12",
        width: 864,
        height: 1536,
        aspect_ratio: "9:16",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-12.jpg",
        variant: 'tall',
        prompt: "Rooftop infinity pool at Address Sky View with Burj Khalifa in background, sunset, cinematic ultra-realistic"
    },
    {
        id: "qwen-image-17",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-17.jpg",

        prompt: "Surreal dark art photography, high‑contrast black‑and‑white, abstract horror aesthetic, close‑up composition, wolf snarling with sharp teeth overlapping a human face, blending of animal and human forms, eerie fusion of predator and softness, grainy film texture, vintage photo effect, unsettling atmosphere, dreamlike nightmare imagery, experimental editorial style, haunting symbolic portrait, chiaroscuro lighting, distorted perspective."
    },
    {
        id: "qwen-image-14",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-14.jpg",

        prompt: "Animate the butterfly wings, natural flapping, shimmering light reflections moving across the crystal facets."
    },
    {
        id: "qwen-image-15",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-15.jpg",

        prompt: "A 25-year-old college jock with a buzzcut, wearing a dark-blue, shiny, latex Adidas bodysuit with three stripes, seated in a modern, concrete, 1960s airport lounge designed by Saarinen, featuring a dark-blue carpet. The scene is captured in a zoomed photograph with rays of sun."
    },
    {
        id: "qwen-image-16",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-16.jpg",
        variant: 'wide',
        prompt: "A stunningly detailed portrait of a beautiful woman in graffiti art style, featuring bold black and red geometric shapes and intricate typographic patterns covering her face and body. Inspired by street art and neo-expressionism, the composition uses a black-and-white color palette with accents of red. The woman's face is detailed, with striking eyes and black lips. The image is rendered in sharp focus, highly detailed, using Octane Render and Unreal Engine 5 at 8K resolution."
    },

    {
        id: "qwen-image-13",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-13.jpg",

        prompt: "Realistic 4K photograph of workers (men and women) each carrying a translucent bucket that occupies about 20% of their bodies. The bucket emits a green Georgia glow. The workers are stacking the buckets together in a tower, in a very orderly fashion, with no sense of play; the scene appears serious and organized. The people, wearing red shirts and khaki pants, look happy working as a team. The background is white, and they are shown in full‑body 4K."
    },
];

const textImageToImageGalleryItems: ImageGalleryItem[] = [
    {
        id: "qwen-image-18",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-18.jpg",

        prompt: "Make the tiger in anime style; the tiger smiles and looks cute",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-9.jpg"]
    },
    {
        id: "qwen-image-19",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-19.jpg",

        prompt: "remove the young cat",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-4.jpg"]
    },
    {
        id: "qwen-image-20",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-20.jpg",

        prompt: "Remove the 'Bar Fuel' product and replace it with playstation controller",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-10.jpg"]
    },
    {
        id: "qwen-image-21",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-21.jpg",

        prompt: "Change the dress to be white",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-1.jpg"]
    },
    {
        id: "qwen-image-22",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-22.jpg",

        prompt: "Zoom out of this image and show the woman in a full-body view.",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-16.jpg"]
    },
    {
        id: "qwen-image-23",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-24.jpg",

        prompt: "Transform the original sketch into colorful, stunning 3D interior designs.",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-23.jpg"]
    },
    {
        id: "qwen-image-24",
        width: 1536,
        height: 864,
        aspect_ratio: "16:9",
        src: "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-26.jpg",

        prompt: "Transform the original sketch into an engineered 3D interior design for advertising.",
        referenceImages: ["https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-25.jpg"]
    }

];

const allTextImageToImage: string[] = [
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-3.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-4.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-5.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-7.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-8.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-9.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-13.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-14.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-15.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-16.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-17.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-18.jpg",
    "https://cdn.aibrush.co/docs/images/models/qwen-image/gallery-23.jpg"
];

export default function QwenImagePage() {
    return (
        <div {...stylex.props(styles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection)}>
                <div {...stylex.props(styles.heroBackground)}>
                    <img
                        src={MODEL_CONFIG.heroBgImage}
                        {...stylex.props(styles.heroBgImage)}
                        alt={`${MODEL_CONFIG.name} hero image`}
                    />
                    <div {...stylex.props(styles.heroOverlay)}></div>
                </div>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Best for <strong>Fast prototyping</strong>, our most affordable model. <strong>Text to Image</strong> and <strong>Text & Image to Image</strong> capabilities.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text to Image</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span>Text & Image to Image</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Image size={20} />
                            <span><a href="/controlnet" style={{ color: 'inherit', textDecoration: 'none' }}>Supports ControlNet</a></span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerImage} credits per image</div>
                </div>
            </section>

            {/* Image Gallery */}
            <TextToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text to Image Gallery"
                description={`Explore the exceptional capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a> through these AI-generated images. this model excels in <strong>text rendering</strong>, <strong>versatile artistic styles</strong>. <strong>Click on any image</strong> to see how it was created.`}
                items={textToImageGalleryItems} />

            {/* Text & Image to Image Gallery */}
            <TextImageToImageGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerImage} credits</strong> per image.`}
                title="Text & Image to Image Gallery"
                description={`Experience advanced image editing with <a href='${MODEL_CONFIG.providerEditUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a> in-context editing capabilities. This model excels at <strong>style transfer</strong>, <strong>object manipulation</strong>, and <strong>detail enhancement</strong> while maintaining the original image's core structure. <strong>Click on any image</strong> to see how it was created.`}
                items={textImageToImageGalleryItems}
                allImages={allTextImageToImage}
            />
        </div>
    )
}
